import { AnyAri } from '@atlassian/ari/any-ari';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createActionsHook,
	createStateHook,
	createContainer,
	type BoundActions,
	type HookActionsFunction,
	type HookStateFunction,
	type StoreActionApi,
	type OverrideContainerComponent,
} from '@atlassian/react-sweet-state';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { JiraProjectAri } from '@atlassian/ari/jira/project';
import {
	PolarisEnvironmentContainerTypes,
	type PolarisEnvironmentContainer,
} from './PolarisEnvironmentContainer.types.tsx';

type ProjectId = string;

type State = {
	loading: boolean;
	error?: string;
	data: PolarisEnvironmentContainer | undefined;
};

type Props = {
	projectId: ProjectId;
	projectKey: string;
	projectAri: JiraProjectAri;
};

type Actions = {
	setLoading: (loading: boolean) => ({ setState }: StoreActionApi<State>) => void;
	setError: (error: string | undefined) => ({ setState }: StoreActionApi<State>) => void;
	setData: (
		data: PolarisEnvironmentContainer | undefined,
	) => ({ setState }: StoreActionApi<State>) => void;
	setProjectIdForView: (
		projectId: ProjectId,
	) => ({ setState, getState }: StoreActionApi<State>) => void;
	reset: () => ({ setState }: StoreActionApi<State>) => void;
};

const initialState: State = {
	loading: true,
	data: undefined,
	error: undefined,
};

const Store = createStore({
	initialState,
	actions: {
		setLoading:
			(loading: boolean): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ loading });
			},
		setError:
			(error: string | undefined): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ error });
			},
		setData:
			(
				data: PolarisEnvironmentContainer | undefined,
			): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({ data });
			},
		setProjectIdForView:
			(projectId: ProjectId): (({ setState, getState }: StoreActionApi<State>) => void) =>
			({ setState, getState }) => {
				const data = getState().data;

				if (data?.type === PolarisEnvironmentContainerTypes.VIEW) {
					setState({ data: { ...data, projectId } });
				}
			},
		reset:
			(): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState(initialState);
			},
	},
	name: 'jpd-environment-datacontainer',
});

const onPropsChange = (
	{ setState }: StoreActionApi<State>,
	{ projectAri, projectId, projectKey }: Props,
) => {
	setState({
		loading: false,
		data: {
			id: projectId,
			type: PolarisEnvironmentContainerTypes.PROJECT,
			projectId,
			projectKey,
			projectAri,
		},
	});
};

export const PolarisProjectEnvironmentContainer: OverrideContainerComponent<Props> =
	createContainer<State, Actions, Props>(Store, {
		onInit: () => onPropsChange,
		onUpdate: () => onPropsChange,
	});

export const useActions: HookActionsFunction<BoundActions<State, Actions>> =
	createActionsHook(Store);

export const useEnvironmentContainer: HookStateFunction<
	PolarisEnvironmentContainer | undefined,
	void
> = createStateHook(Store, { selector: (state) => state.data });

export const useEnvironmentContainerId: HookStateFunction<string | undefined, void> =
	createStateHook(Store, {
		selector: (state) => state.data?.id,
	});

export const useContainerAri = (): string => {
	const cloudId = useCloudId();
	const container = useEnvironmentContainer();

	const createARI = (resourceType: string, resourceId: string) =>
		AnyAri.create({
			resourceOwner: 'jira',
			cloudId,
			resourceType,
			resourceId,
		}).toString();

	switch (container?.type) {
		case PolarisEnvironmentContainerTypes.PROJECT:
			return container.projectAri.toString();
		case PolarisEnvironmentContainerTypes.COLLECTION:
			return createARI('collection', container.collectionId);
		case PolarisEnvironmentContainerTypes.VIEW:
			return createARI('view', container.viewId);
		default:
			return '';
	}
};

export const useHasEnvironmentContainerError: HookStateFunction<string | boolean, void> =
	createStateHook(Store, {
		selector: (state) => !state.loading && (state.error || !state.data),
	});

export const useIsEnvironmentContainerAvailable: HookStateFunction<
	boolean,
	'PROJECT' | 'VIEW' | 'COLLECTION' | undefined
> = createStateHook(Store, {
	selector: (state, containerType?: PolarisEnvironmentContainer['type']) => {
		if (containerType) {
			return !state.loading && state.data !== undefined && state.data.type === containerType;
		}

		return !state.loading && state.data !== undefined;
	},
});
